import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Modal, Spin, Tabs, message } from 'antd';
import { CheckOutlined, GiftOutlined, WalletOutlined } from '@ant-design/icons';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { get, post } from '../../../../../axios/axios';
import EightPayModal from '../../../../EightPieceSet/components/EightPayModal/EightPayModal';
import GroupBuyingOrder from '../../../../../components/GroupBuyingOrder/GroupBuyingOrder';
import {
    rememberPackageOrder,
    selectPackageEntitlement,
} from '../../../../../Service/packageEntitlements';

const displayPrice = (value) =>
    value !== null &&
    value !== undefined &&
    value !== '' &&
    Number.isFinite(Number(value)) &&
    Number(value) >= 0
        ? `¥${Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`
        : '购买时确认价格';

const PackageModal = NiceModal.create(() => {
    const modal = useModal();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buyingId, setBuyingId] = useState(null);
    const busy = useRef(false);

    useEffect(() => {
        if (!modal.visible) return undefined;
        let active = true;
        setLoading(true);
        get('/member/order/packages/list')
            .then((res) => {
                if (!res.success) throw new Error(res.msg || '套餐暂时无法加载');
                if (active) setGroups(res.data?.packages || []);
            })
            .catch((error) => {
                if (active) message.error(error.message || '套餐暂时无法加载');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [modal.visible]);

    const chooseEntitlement = (record) => {
        selectPackageEntitlement(record);
        modal.resolve(record);
        modal.hide();
    };
    const openMyPackage = (props = {}) =>
        NiceModal.show(GroupBuyingOrder, { ...props, onSelect: chooseEntitlement }).catch(() => {});

    const handleBuy = async (pkg) => {
        if (busy.current) return;
        busy.current = true;
        setBuyingId(pkg.id);
        try {
            const res = await post('/member/payment/order', { idf: pkg.id });
            if (
                !res.success ||
                !res.data?.orderNo ||
                displayPrice(res.data.price) === '购买时确认价格'
            ) {
                throw new Error(res.msg || '创建套餐订单失败，请重试');
            }
            const { orderNo, price } = res.data;
            rememberPackageOrder(orderNo);
            const result = await NiceModal.show(EightPayModal, {
                orderNo,
                price,
                majorName: pkg.title,
                packageType: pkg.id === 't11' ? 11 : '',
                zIndex: 1892,
                preserveContext: true,
            });
            if (result?.paid) {
                rememberPackageOrder(orderNo);
                message.success('套餐购买成功，选择一份权益用于当前内容');
                openMyPackage({ initialOrderNo: orderNo, paymentCompleted: true });
            }
        } catch (error) {
            message.error(error.message || '暂时无法购买套餐，请重试');
        } finally {
            busy.current = false;
            setBuyingId(null);
        }
    };

    return (
        <Modal
            title={
                <span>
                    <GiftOutlined /> 购买创作套餐
                </span>
            }
            open={modal.visible}
            width={960}
            footer={null}
            zIndex={1891}
            onCancel={() => {
                modal.reject();
                modal.hide();
            }}
            maskClosable={false}
        >
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <p className="m-0 text-sm text-[#647087]">
                    购买后直接选择权益，继续使用当前题目和大纲。
                </p>
                <Button icon={<WalletOutlined />} onClick={() => openMyPackage()}>
                    我的套餐
                </Button>
            </div>
            <Spin spinning={loading}>
                {groups.length ? (
                    <Tabs
                        items={groups.map((group, index) => ({
                            key: `${group.type}-${index}`,
                            label: group.type,
                            children: (
                                <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-3">
                                    {(group.items || []).map((pkg) => (
                                        <article
                                            key={pkg.id}
                                            className="flex flex-col rounded-xl border border-[#e9e4f0] bg-white p-5"
                                        >
                                            <h3 className="mb-4 text-base font-semibold text-[#192038]">
                                                {pkg.title}
                                            </h3>
                                            <div className="mb-5 flex-1 space-y-3">
                                                {(pkg.contents || []).map((item, itemIndex) => (
                                                    <div
                                                        key={`${item.name}-${itemIndex}`}
                                                        className="flex items-baseline justify-between gap-3 text-sm"
                                                    >
                                                        <span>
                                                            <CheckOutlined className="mr-2 text-primary" />
                                                            {item.name}
                                                        </span>
                                                        <span className="shrink-0 text-xs text-[#647087]">
                                                            {item.quantity}
                                                            {item.unit}
                                                        </span>
                                                    </div>
                                                ))}
                                                {/优惠[券卷]/.test(group.type) && (
                                                    <p className="text-sm text-[#647087]">
                                                        优惠券用于符合条件的文章订单抵扣，具体规则以使用时查询结果为准。
                                                    </p>
                                                )}
                                            </div>
                                            <div className="mb-4 text-2xl font-semibold text-primary">
                                                {displayPrice(pkg.price)}
                                            </div>
                                            <Button
                                                type="primary"
                                                block
                                                loading={buyingId === pkg.id}
                                                disabled={buyingId !== null && buyingId !== pkg.id}
                                                onClick={() => handleBuy(pkg)}
                                            >
                                                购买此套餐
                                            </Button>
                                        </article>
                                    ))}
                                </div>
                            ),
                        }))}
                    />
                ) : (
                    !loading && <Empty description="暂未获取到可购买的套餐" />
                )}
            </Spin>
            <Alert
                className="mt-4"
                type="info"
                showIcon
                message="已有套餐可直接使用，无需再次购买"
            />
        </Modal>
    );
});

export default PackageModal;
